import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Map as MapIcon,
  LocationOn,
  AttachMoney,
  Home,
  Search,
  Close,
  Info
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from './constants';
import axios from 'axios';

// Dynamically import Leaflet to avoid SSR issues
let L = null;
let MapContainer = null;
let TileLayer = null;
let Marker = null;
let Popup = null;
let useMap = null;

const RealEstateMapView = () => {
  const { projectKey } = useParams();
  const navigate = useNavigate();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mapCenter, setMapCenter] = useState([49.8175, 15.4730]); // Center of Czech Republic
  const [mapZoom, setMapZoom] = useState(7);

  // Load Leaflet dynamically
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        // Import Leaflet CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);

        // Import Leaflet JS
        const leafletScript = document.createElement('script');
        leafletScript.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        leafletScript.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        leafletScript.crossOrigin = '';
        leafletScript.onload = () => {
          L = window.L;
          setMapLoaded(true);
        };
        document.head.appendChild(leafletScript);
      } catch (err) {
        console.error('Error loading Leaflet:', err);
        setError('Failed to load map library');
      }
    };

    loadLeaflet();
  }, []);

  // Fetch listings data
  useEffect(() => {
    if (mapLoaded) {
      fetchListings();
    }
  }, [mapLoaded]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch detailed data - limit to 100 for initial load (geocoding takes time)
      const response = await axios.get(`${API_URL}/real_estate/sreality/detailed-data`, {
        params: {
          limit: 100,
          offset: 0
        }
      });

      if (response.data && response.data.data) {
        const columns = response.data.columns || [];
        const data = response.data.data;

        // Process listings and geocode addresses progressively
        processListingsProgressive(data, columns);
      }
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to load listings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const processListingsProgressive = async (data, columns) => {
    const addressIndex = columns.indexOf('address');
    const idIndex = columns.indexOf('id');
    const hashIdIndex = columns.indexOf('hash_id');
    const priceIndex = columns.indexOf('price');
    const titleIndex = columns.indexOf('title');
    const descriptionIndex = columns.indexOf('description');
    const mainPictureIndex = columns.indexOf('main_picture');

    // Prepare listings for bulk geocoding
    const listingsToGeocode = [];
    const listingData = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const address = addressIndex >= 0 ? row[addressIndex] : null;
      const id = idIndex >= 0 ? row[idIndex] : (hashIdIndex >= 0 ? row[hashIdIndex] : i);
      const hashId = hashIdIndex >= 0 ? row[hashIdIndex] : id;
      
      if (!address) continue;

      listingsToGeocode.push({
        hash_id: String(hashId),
        address: address
      });

      listingData.push({
        id: id,
        hashId: hashId,
        address: address,
        price: priceIndex >= 0 ? row[priceIndex] : null,
        title: titleIndex >= 0 ? row[titleIndex] : null,
        description: descriptionIndex >= 0 ? row[descriptionIndex] : null,
        mainPicture: mainPictureIndex >= 0 ? row[mainPictureIndex] : null,
        rowData: row,
        columns: columns
      });
    }

    if (listingsToGeocode.length === 0) {
      setListings([]);
      return;
    }

    try {
      // Bulk geocode all listings at once
      const geocodeResponse = await axios.post(
        `${API_URL}/real_estate/enrich/bulk-geocode`,
        { listings: listingsToGeocode },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (geocodeResponse.data && geocodeResponse.data.results) {
        const geocodeResults = geocodeResponse.data.results;
        const processed = [];

        // Match geocoding results with listing data
        for (let i = 0; i < geocodeResults.length; i++) {
          const geocodeResult = geocodeResults[i];
          const listingInfo = listingData[i];

          if (geocodeResult && geocodeResult.found && geocodeResult.latitude && geocodeResult.longitude) {
            processed.push({
              id: listingInfo.id,
              address: listingInfo.address,
              latitude: geocodeResult.latitude,
              longitude: geocodeResult.longitude,
              price: listingInfo.price,
              title: listingInfo.title,
              description: listingInfo.description,
              mainPicture: listingInfo.mainPicture,
              rowData: listingInfo.rowData,
              columns: listingInfo.columns,
              cached: geocodeResult.cached || false
            });
          }
        }

        setListings(processed);
        
        console.log(`Geocoded ${geocodeResponse.data.geocoded} new addresses, used ${geocodeResponse.data.cached} cached results`);
      }
    } catch (err) {
      console.error('Error bulk geocoding listings:', err);
      setError('Failed to geocode addresses. Please try again later.');
    }
  };

  // Initialize map and markers
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapLoaded || !L) return;

    // Initialize map only once
    if (!mapRef.current) {
      const map = L.map('real-estate-map').setView(mapCenter, mapZoom);

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(map);

      mapRef.current = map;

      // Make openListing function available globally
      window.openListing = (id) => {
        navigate(`/${projectKey}/real_estate/${id}`);
      };
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (mapRef.current) {
        mapRef.current.removeLayer(marker);
      }
    });
    markersRef.current = [];

    // Add markers for each listing
    if (mapRef.current) {
      listings.forEach(listing => {
        if (listing.latitude && listing.longitude) {
          const marker = L.marker([listing.latitude, listing.longitude])
            .addTo(mapRef.current)
            .bindPopup(`
              <div style="min-width: 200px;">
                <h3 style="margin: 0 0 10px 0; font-size: 16px;">${(listing.title || 'Real Estate Listing').replace(/"/g, '&quot;')}</h3>
                <p style="margin: 5px 0; font-size: 12px; color: #666;">${(listing.address || '').replace(/"/g, '&quot;')}</p>
                ${listing.price ? `<p style="margin: 5px 0; font-size: 14px; font-weight: bold; color: #2e7d32;">${formatPrice(listing.price)}</p>` : ''}
                <button onclick="window.openListing(${listing.id})" style="margin-top: 10px; padding: 5px 10px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;">
                  View Details
                </button>
              </div>
            `);

          marker.on('click', () => {
            setSelectedListing(listing);
            setDialogOpen(true);
          });

          markersRef.current.push(marker);
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      delete window.openListing;
    };
  }, [mapLoaded, listings, navigate, projectKey]);

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedListing(null);
  };

  const handleViewDetails = () => {
    if (selectedListing) {
      navigate(`/${projectKey}/real_estate/${selectedListing.id}`);
    }
  };

  const filteredListings = listings.filter(listing => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      listing.address?.toLowerCase().includes(search) ||
      listing.title?.toLowerCase().includes(search) ||
      String(listing.price || '').includes(search)
    );
  });

  if (!mapLoaded) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <MapIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Real Estate Map - Czech Republic
            </Typography>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by address, title, or price..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={`${listings.length} Listings`}
              color="primary"
              icon={<Home />}
            />
            <Chip
              label={`${filteredListings.length} Visible`}
              color="secondary"
            />
            {loading && (
              <Chip
                label="Loading..."
                icon={<CircularProgress size={16} />}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Map Container */}
      <Paper elevation={3} sx={{ height: '600px', position: 'relative', mb: 3 }}>
        <div
          id="real-estate-map"
          style={{
            width: '100%',
            height: '100%',
            zIndex: 0
          }}
        />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: 2,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress size={24} />
            <Typography>Loading listings and geocoding addresses...</Typography>
          </Box>
        )}
      </Paper>

      {/* Listing Summary */}
      {filteredListings.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Listings ({filteredListings.length})
            </Typography>
            <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {filteredListings.map((listing) => (
                <Paper
                  key={listing.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => {
                    setSelectedListing(listing);
                    setDialogOpen(true);
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {listing.title || 'Real Estate Listing'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {listing.address}
                      </Typography>
                      {listing.price && (
                        <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          <AttachMoney fontSize="small" sx={{ verticalAlign: 'middle' }} />
                          {formatPrice(listing.price)}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${projectKey}/real_estate/${listing.id}`);
                      }}
                    >
                      View
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Listing Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedListing?.title || 'Real Estate Listing'}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedListing && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {selectedListing.address}
              </Typography>
              
              {selectedListing.price && (
                <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 'bold', mb: 2 }}>
                  <AttachMoney />
                  {formatPrice(selectedListing.price)}
                </Typography>
              )}

              {selectedListing.description && (
                <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {selectedListing.description}
                </Typography>
              )}

              {selectedListing.mainPicture && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={selectedListing.mainPicture}
                    alt="Property"
                    style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Chip
                  label={`Lat: ${selectedListing.latitude?.toFixed(4)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Lng: ${selectedListing.longitude?.toFixed(4)}`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button onClick={handleViewDetails} variant="contained" color="primary">
            View Full Details
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RealEstateMapView;

